import { EventEmitter, Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { AuthResponseDto } from '../../models/auth/auth-response-dto';
import { AuthRequestDto } from '../../models/auth/auth-request-dto';
import { jwtDecode } from 'jwt-decode';
import { Router } from '@angular/router';
import { MineTokenDto } from '../../models/mine/mine-token-dto';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  apiUrl: string = environment.apiUrl;
  isLoggedEmitter = new EventEmitter<boolean>();
  permissions: { moduleId: string }[] = [];

  constructor(private http: HttpClient, private router: Router) {}

  authenticate(auth: AuthRequestDto) {
    return this.http.post<AuthResponseDto>(`${this.apiUrl}/api/auth`, auth);
  }

  setLanguage(language: string) {
    localStorage.setItem('language', language);
  }

  getLanguage(): string {
    return localStorage.getItem('language');
  }

  setAuthentication(authResponse: AuthResponseDto) {
    localStorage.setItem(environment.keyToken, JSON.stringify(authResponse));
    let mines: MineTokenDto[] = this.getMines();
    if (mines.length > 0) {
      localStorage.setItem(environment.keySelectedMine, JSON.stringify(mines[0]));
    }
    this.isLoggedEmitter.emit(true);
  }

  private hasToken(): boolean {
    return localStorage.getItem(environment.keyToken) != null;
  }

  getToken(): string {
    return this.hasToken() ? JSON.parse(localStorage.getItem(environment.keyToken)).access_token : '';
  }

  getMines(): MineTokenDto[] {
    return this.hasToken() ? this.getDecodedToken().mines : null;
  }

  getCountryId(): string {
    return this.hasToken() ? this.getDecodedToken().countryId : '';
  }

  isResetPassword(): boolean {
    return this.hasToken() ? this.getDecodedToken().resetPassword : false;
  }

  getSelectedMine(): MineTokenDto {
    return this.hasToken() ? JSON.parse(localStorage.getItem(environment.keySelectedMine)) : null;
  }

  getDecodedToken(): any {
    return this.hasToken() ? jwtDecode(this.getToken()) : '';
  }

  getUsername(): string {
    return this.hasToken() ? this.getDecodedToken().username : '';
  }

  getName(): string {
    return this.hasToken() ? this.getDecodedToken().name : '';
  }

  getRoles(): any {
    return this.hasToken() ? this.getDecodedToken().roles : '';
  }

  isLoggedIn(): boolean {
    if (this.getDecodedToken() == '') return false;

    return !this.isTokenExpired();
  }

  isTokenExpired(): boolean {
    const decodedToken = this.getDecodedToken();
    if (!decodedToken) {
      return true;
    }
    const currentTime = Math.floor(Date.now() / 1000);
    return decodedToken.exp < currentTime;
  }

  logout() {
    localStorage.removeItem(environment.keySelectedMine);
    localStorage.removeItem(environment.keyToken);
    localStorage.removeItem(environment.keyPermissions);
    this.router.navigate(['/login']);
  }

  hasPermission(moduleId: string): boolean {
    const storedPermissions: string = localStorage.getItem(environment.keyPermissions);
    if (storedPermissions) {
      this.permissions = JSON.parse(storedPermissions);
    }
    return this.permissions.some((permission) => permission.moduleId === moduleId);
  }
}
